import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  createContext,
  useContext,
} from "react";
import { createPortal } from "react-dom";
import { cn } from "lib/utils";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  KeyboardSensor,
  MeasuringStrategy,
  useSensor,
  useSensors,
  useDroppable,
} from "@dnd-kit/core";
import { isContainerType, dndCollision, resolveDropTarget } from "./planDnd";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import {
  ChevronDown,
  ChevronRight,
  Clock,
  ArrowRight,
  MapPin,
  Plus,
  Info,
  Trash2,
  Copy,
  Pencil,
  GripVertical,
  MoreVertical,
  Check,
  Layers,
  AlertTriangle,
  Split,
  Ungroup,
} from "lucide-react";
import { Button } from "../ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "../ui/context-menu";
import { useToast } from "../../contexts/ToastContext";
import {
  toSeconds,
  formatDuration,
  formatDurationCompact,
} from "../../utils/time";
import {
  isTransitionLabel,
  isHiddenInSummary,
  isSetupNode,
  formatSetSchemeLoads,
  blockingValidationIssues,
} from "../../utils/planVisuals";
import { formatIntervalData } from "./planIntervalFormat";
import { isCountedUnit } from "../../utils/planNodeUtils";
import {
  enclosingSetCount,
  genStableId,
  sharedParentPath,
  isSplittableGroup,
  isUngroupableNode,
} from "../../utils/planEditing";
import { useSwipeActions } from "../../hooks/useSwipeActions";
import useTotalDuration, {
  useGroupRoundSummary,
} from "../../hooks/useTotalDuration";
import useRepEstimate from "../../hooks/useRepEstimate";
import useWorkoutSettings from "../../hooks/useWorkoutSettings";
import { useUserProfile } from "../../contexts/UserProfileContext";
import { formatLabel } from "../../utils/formatLabel";
import useMediaQuery from "../../hooks/useMediaQuery";
import ExerciseDetailDialog from "./ExerciseDetailDialog";
import SplitRoundsSheet from "./SplitRoundsSheet";
import SetupSheet from "./SetupSheet";
import { getVisualsForType, getVisualsForNode } from "./planTheme";

export { getVisualsForType };

const bgShades = ["bg-card/80", "bg-card/60", "bg-card/40", "bg-card/20"];

// Live drop-target hint, resolved on every drag move with the SAME logic that
// decides the final drop (resolveDropTarget). Rows/gaps subscribe to render an
// insertion line or a nest-into ring, so what users see is what they get.
// Shape: { kind: "gap", parentKey, index } | { kind: "into", key } | null.
const DropHintContext = createContext(null);

// Multi-select for "group these into a block". Held in context rather than
// threaded through the recursive IntervalCard, which already carries eight
// props; the drop hint set the precedent. Null when the surface can't wrap
// (no onWrapNodes), so the row menus can hide the entry point.
// Shape: { active, selected: Set<pathKey>, canSelect(indexPath), start(indexPath), toggle(indexPath) }.
const SelectionContext = createContext(null);

// Node ids that can no longer change: steps already finished in a session
// being edited mid-workout (#175). Their rows render dimmed and every edit
// gesture stands down; the record of what was done is not up for revision.
const LockedContext = createContext(null);

// Block actions that change the tree's shape, null when the surface can't
// make them: `split(indexPath, node)` opens the round-naming sheet for a
// repeating block, `ungroup(indexPath)` removes a block that runs once.
const SplitContext = createContext(null);

// Opens the Setup sheet for the divider at an index path, null when the plan
// isn't editable. Setups are placed by the app, so a tap is the only gesture
// they take: no drag handle, no ⋯ menu.
// Shape: { open(indexPath), restore(ownerIndexPath) }.
const SetupContext = createContext(null);

// --- Components ---

// Section-divider treatment for Setup sections (inter-block "Setup…" recovery
// nodes — dedicated time to set up new equipment between blocks). These sit
// between top-level Group blocks and visually punctuate the workout into
// chapters rather than enumerating as one more row. Plain transitions are
// hidden entirely (issue #22); only Setup sections get a divider.
const SetupDivider = ({ node, indexPath }) => {
  const setup = useContext(SetupContext);
  const lockedIds = useContext(LockedContext);
  const seconds = toSeconds(node.value, node.unit);
  const compact = formatDurationCompact(seconds);
  const label = node.label || "Setup";
  const locked = Boolean(lockedIds && node.id != null && lockedIds.has(node.id));
  const tappable = Boolean(setup && indexPath && !locked);
  const chip = (
    <div
      className={cn(
        "flex items-center gap-2 text-[11px] uppercase tracking-wider text-transition",
        tappable &&
          "rounded-full border border-transition/40 px-2.5 py-1 group-hover:border-transition group-focus-visible:border-transition",
      )}
    >
      <ArrowRight className="w-3.5 h-3.5" />
      <span className="font-medium">{label}</span>
      {compact && (
        <>
          <span className="opacity-60">·</span>
          <span className="tabular-nums">{compact}</span>
        </>
      )}
    </div>
  );
  const line = <div className="flex-1 h-px bg-transition/30" />;
  if (tappable) {
    return (
      <button
        type="button"
        onClick={() => setup.open(indexPath)}
        className="group flex min-h-11 w-full items-center gap-3 py-1 outline-none"
        aria-label={`${label} · ${compact}. Change or skip`}
      >
        {line}
        {chip}
        {line}
      </button>
    );
  }
  return (
    <div
      className={cn("flex items-center gap-3 py-2", locked && "opacity-50")}
      aria-label={`${label} · ${compact}`}
    >
      {line}
      {chip}
      {line}
    </div>
  );
};

// Where a skipped Setup would sit, in an editable plan only: a faint line that
// puts it back. Without it a skip could never be undone, since there is no
// node left to tap.
const SkippedSetup = ({ owner, ownerIndexPath }) => {
  const setup = useContext(SetupContext);
  const lockedIds = useContext(LockedContext);
  if (!setup || !owner?.skipSetup) return null;
  if (lockedIds && owner.id != null && lockedIds.has(owner.id)) return null;
  return (
    <button
      type="button"
      onClick={() => setup.restore(ownerIndexPath)}
      className="group flex min-h-11 w-full items-center gap-3 py-1 outline-none"
    >
      <div className="flex-1 border-t border-dashed border-transition/30" />
      <span className="text-[11px] uppercase tracking-wider text-muted-foreground group-hover:text-foreground group-focus-visible:text-foreground">
        Setup skipped · Add back
      </span>
      <div className="flex-1 border-t border-dashed border-transition/30" />
    </button>
  );
};

// Thin row-gap with a hover-revealed "+" button for inserting a new step at
// this position, plus a dnd-kit droppable zone so users can drop a dragged
// row at exactly this insertion point. The indicator bar is driven by the
// shared drop hint (not raw isOver), so it also lights up when a row-band
// hover resolves to this insertion point — the bar always marks where the
// drop will actually land.
const RowGap = ({ onInsert, parentIndexPath, insertIndex, droppable }) => {
  const droppableId = droppable
    ? `gap-${parentIndexPath.join(".")}-${insertIndex}`
    : null;
  const { setNodeRef } = useDroppable({
    id: droppableId || "gap-disabled",
    data: { kind: "gap", parentIndexPath, insertIndex },
    disabled: !droppable,
  });
  const dropHint = useContext(DropHintContext);
  const isDropTarget =
    dropHint?.kind === "gap" &&
    dropHint.parentKey === parentIndexPath.join(".") &&
    dropHint.index === insertIndex;

  if (!onInsert && !droppable) return <div className="h-2" />;

  return (
    <div
      ref={droppable ? setNodeRef : undefined}
      className="relative h-2 group/gap z-10"
    >
      {/* Drop indicator: 2px primary bar marking the resolved insertion point. */}
      {isDropTarget && (
        <span
          className="absolute inset-x-2 top-1/2 -translate-y-1/2 h-0.5 bg-primary rounded-full pointer-events-none"
          aria-hidden
        />
      )}
      {onInsert && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onInsert();
          }}
          aria-label="Insert step here"
          className="absolute inset-x-0 inset-y-0 flex items-center justify-center opacity-0 group-hover/gap:opacity-100 transition-opacity"
        >
          <span className="absolute inset-x-2 h-px bg-primary/40" />
          <span className="relative flex items-center justify-center w-5 h-5 rounded-full bg-card border border-primary/60 text-primary shadow-sm">
            <Plus className="w-3 h-3" />
          </span>
        </button>
      )}
    </div>
  );
};

// `schemeValues` is the per-set `value` ramp when the node carries one. The
// estimate hook only ever sees set 1, so a ramped exercise is summed here —
// otherwise the chip reads "8/7/6/5 reps" and the popover explains 8 of them.
// Per-rep pace is held at set 1's; it shifts with load across a ramp, but
// re-deriving it per set would mean re-running the estimator four times for a
// tooltip.
const RepEstimatePopover = ({
  children,
  repEstimate,
  value,
  unit,
  schemeValues,
}) => {
  if (!repEstimate || repEstimate.totalSec < 5) return children;
  const perRep = repEstimate.perRepSec.toFixed(1);
  // "per rep" / "per cal" — the estimate is per UNIT, and a calorie target
  // labelled "per rep" reads as a different prescription entirely.
  const perUnitLabel = repEstimate.unit === "cal" ? "cal" : "rep";
  const schemeReps = Array.isArray(schemeValues)
    ? schemeValues.filter(Number.isFinite).reduce((sum, v) => sum + v, 0)
    : 0;
  const totalReps = schemeReps > 0 ? schemeReps : null;
  const total = Math.round(
    totalReps != null ? repEstimate.perRepSec * totalReps : repEstimate.totalSec,
  );
  const sourceLabel =
    {
      history: "your recent history",
      blended: "blended history + formula",
      formula: "load/level formula",
      ergo: "machine calorie rate",
      fallback: "generic estimate",
    }[repEstimate.source] || repEstimate.source;
  return (
    <Popover>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        side="top"
        align="end"
        className="z-[70] w-60 p-3 text-xs"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="font-semibold text-foreground mb-1 flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" />
          Estimated {total}s
        </div>
        <div className="text-muted-foreground tabular-nums">
          {totalReps != null
            ? `${totalReps} ${unit} across ${schemeValues.length} sets`
            : `${value} ${unit}`}{" "}
          × ~{perRep}s per {perUnitLabel}
        </div>
        <div className="text-muted-foreground mt-1 opacity-80">
          Source: {sourceLabel}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Round durations as a compact run-length list: [60,60,60,50] reads
// "3× 1:00 · 0:50" rather than repeating an identical figure four times.
const summariseRoundList = (roundDurations) => {
  const runs = [];
  for (const sec of roundDurations) {
    const label = formatDurationCompact(sec) || "0:00";
    const last = runs[runs.length - 1];
    if (last && last.label === label) last.count += 1;
    else runs.push({ label, count: 1 });
  }
  return runs
    .map(({ label, count }) => (count > 1 ? `${count}× ${label}` : label))
    .join(" · ");
};

// Explains a block's clock (issue #73). The header can only show one round, but
// rounds are rarely equal and some of the time they contain is deliberately not
// rendered as a row: injected changeovers are hidden (issue #22) and the final
// round's trailing rest is stripped by the compiler. Both are stated here so
// the visible rows and the total reconcile. A Popover rather than a `title`
// tooltip because the plan view is used on a phone, where hover does not exist.
const BlockTimePopover = ({ children, summary, totalLabel, disabled }) => {
  const { roundDurations, changeoverSec, droppedRestSec } = summary;
  if (disabled || roundDurations.length === 0) return children;
  const changeoverLabel = formatDurationCompact(changeoverSec);
  const droppedLabel = formatDurationCompact(droppedRestSec);
  return (
    <Popover>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        side="top"
        align="end"
        className="z-[70] w-64 p-3 text-xs"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="font-semibold text-foreground mb-1 flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" />
          {totalLabel} total
        </div>
        <div className="text-muted-foreground tabular-nums">
          {summariseRoundList(roundDurations)}
        </div>
        {(changeoverLabel || droppedLabel) && (
          <div className="mt-1.5 space-y-0.5 text-muted-foreground opacity-80">
            {changeoverLabel && (
              <div>Includes {changeoverLabel} changeover between exercises.</div>
            )}
            {droppedLabel && (
              <div>
                The last round&apos;s {droppedLabel} rest is not counted — the
                block ends there.
              </div>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

const IntervalCard = ({
  interval,
  depth = 0,
  indexPath = [],
  onOpenDetail,
  onInsertNode,
  onDelete,
  onDuplicate,
  sortable = false,
  // When true this card is the floating clone rendered inside <DragOverlay>;
  // the actual in-list copy is hidden via opacity below.
  isDragOverlay = false,
  // While a Group is being dragged, every group renders collapsed (children
  // hidden) so block-level reordering happens over a short, stable list. The
  // per-card isExpanded state is untouched, so expansion is restored on drop.
  collapseGroups = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const lockedIds = useContext(LockedContext);
  const locked = Boolean(
    lockedIds && interval?.id != null && lockedIds.has(interval.id),
  );
  if (locked) {
    onOpenDetail = undefined;
    onInsertNode = undefined;
    onDelete = undefined;
    onDuplicate = undefined;
    sortable = false;
  }

  // dnd-kit bindings. Use a stable id derived from node.id (back-filled by
  // planNormaliser). Disabled for the floating overlay clone and for the
  // immutable macro-divider rows handled at the parent level.
  const hasChildrenForData =
    Array.isArray(interval?.children) && interval.children.length > 0;
  const isContainer = hasChildrenForData || isContainerType(interval?.type);
  const sortableId = interval?.id ?? `card-${indexPath.join(".")}`;
  const sortableData = useMemo(
    () => ({ kind: "row", indexPath, isGroup: isContainer }),
    [indexPath, isContainer],
  );
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    isDragging,
  } = useSortable({
    id: sortableId,
    data: sortableData,
    disabled: !sortable || isDragOverlay,
  });

  // Deliberately no sorting-strategy transform: rows stay put during a drag.
  // The floating DragOverlay clone plus the drop-hint line/ring communicate
  // the landing spot. Strategy shifts animated rows as if this were a flat
  // list, which disagreed with the band-based nesting drop resolution and
  // made drops feel unpredictable (issue #44).
  const sortableStyle = sortable
    ? { opacity: isDragging ? 0.3 : 1 }
    : undefined;

  // Nest-into-group ring, driven by the same resolver as the real drop.
  const dropHint = useContext(DropHintContext);
  const pathKey = indexPath.join(".");
  const isNestTarget =
    !isDragOverlay &&
    dropHint?.kind === "into" &&
    dropHint.key === pathKey;

  // --- Multi-select ("group these into a block") ---
  // While selecting, the row's normal gestures stand down: the tap selects,
  // swipe and drag are off, and the ⋯ menu is hidden. Only siblings of the
  // first pick are selectable — a block has one position in one parent, so a
  // cross-parent selection has no meaning.
  const selection = useContext(SelectionContext);
  const blockActions = useContext(SplitContext);
  const canSplit =
    !locked && Boolean(blockActions?.split) && isSplittableGroup(interval);
  const canUngroup =
    !locked && Boolean(blockActions?.ungroup) && isUngroupableNode(interval);
  const selectionActive = Boolean(selection?.active) && !isDragOverlay;
  const isSelected = selectionActive && selection.selected.has(pathKey);
  const isSelectable =
    selectionActive && !locked && selection.canSelect(indexPath);

  // --- Swipe gestures (touch only) ---
  //
  // Shared with the programme timeline rows via useSwipeActions, so both
  // surfaces have one gesture implementation and one feel. The hook ignores
  // mouse pointers, so a click-drag never hijacks a click.
  //
  // Handlers live on the HEADER ROW div, not the outer card container. For
  // leaves the header row spans the whole card, so behaviour is unchanged;
  // for groups it means (a) a swipe on a child row can't also drag its parent
  // group (children render in a sibling subtree, so nothing bubbles into the
  // header), and (b) pointer-downs on the drag handle never start a swipe
  // (the handle is a sibling of the header row, not an ancestor). Groups get
  // the same swipe-right-to-edit / swipe-left-to-delete as leaves (issue #44).
  //
  // touch-action: pan-y on the header row so vertical scroll still works
  // — the browser keeps native scrolling, JS captures horizontal motion.
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    if (typeof navigator === "undefined") return;
    setIsTouchDevice(navigator.maxTouchPoints > 0);
  }, []);

  const swipeEnabled =
    !isDragOverlay &&
    !selectionActive &&
    (Boolean(onDelete) || Boolean(onOpenDetail));

  const {
    offset: swipeOffset,
    dragging: swiping,
    handlers: swipeHandlers,
    maxReveal: swipeMaxReveal,
  } = useSwipeActions({
    enabled: swipeEnabled,
    onSwipeLeft: onDelete ? () => onDelete(indexPath, interval) : undefined,
    onSwipeRight: onOpenDetail
      ? () => onOpenDetail(interval, indexPath, isContainer)
      : undefined,
  });

  const hasChildren = hasChildrenForData;
  const isLeaf = !isContainer;
  const expanded = isExpanded && !collapseGroups;
  const backgroundClass = bgShades[depth % bgShades.length];

  const rawLabel = interval.label || interval.exercise_name || "Unnamed step";
  const label = formatLabel(rawLabel);
  // getVisualsForNode falls through to getVisualsForType for non-Group/Circuit
  // nodes, so leaves keep their existing behaviour. Groups now derive their
  // theme from children + label (Warmup/Cooldown wins, else priority match).
  const { icon, theme } = getVisualsForNode(interval);
  const intervalTotal = useTotalDuration(interval);
  const totalDuration = hasChildren ? intervalTotal : 0;
  const formattedTotalDuration = formatDuration(totalDuration);
  const { denseString, verboseString } = formatIntervalData(interval);

  const repEstimate = useRepEstimate(
    isLeaf && isCountedUnit(interval.unit) ? interval : null,
  );

  // Varying load across the sets (issue #42). Rendered under the label rather
  // than in the dense column, which stays a single quantity — the reps ramp.
  const perSetLoads = isLeaf ? formatSetSchemeLoads(interval) : "";
  const perSetValues = isLeaf ? interval.setScheme?.value : null;

  // Per-round breakdown for Group blocks with N > 1 iterations.
  const groupUnit = String(interval.unit || "").toLowerCase();
  const rounds = Number(interval.value);
  // Time-capped container (AMRAP / "for time" cap, #36): a Group whose unit is a
  // time unit renders its `value` as the M:SS cap (via denseString), so the
  // separate "total time" clock would just repeat it — suppress the duplicate.
  const isTimeCappedGroup =
    interval.type === "Group" && (groupUnit === "s" || groupUnit === "m");
  const showRoundBreakdown =
    interval.type === "Group" &&
    (groupUnit === "rounds" || groupUnit === "sets") &&
    Number.isFinite(rounds) &&
    rounds > 1 &&
    totalDuration > 0;
  const totalCompact = showRoundBreakdown
    ? formatDurationCompact(totalDuration)
    : null;
  // The per-round figure is round ONE's real duration, not `total / rounds`
  // (issue #73). Rounds are rarely equal — a `setScheme` ramp shortens each set
  // and the compiler strips the final round's trailing rest — so the mean
  // described a round that never runs. `~` marks the rounds that differ; the
  // popover lists them all.
  const roundSummary = useGroupRoundSummary(
    interval.type === "Group" ? interval : null,
  );
  // The summary needs a node id to bucket rounds by. Plans normalised on read
  // always have one; fall back to the mean for anything that slipped through.
  const hasRoundSummary = showRoundBreakdown && roundSummary.rounds > 0;
  const perRoundCompact = showRoundBreakdown
    ? formatDurationCompact(
        hasRoundSummary
          ? roundSummary.roundDurations[0]
          : totalDuration / rounds,
      )
    : null;
  const perRoundApprox = hasRoundSummary && roundSummary.roundsVary;

  const handleToggleExpand = useCallback(() => {
    setIsExpanded((v) => !v);
  }, []);

  // Tap on a group expands/collapses; tap on a leaf is a no-op (the reps
  // chip has its own Popover trigger). Desktop reaches edit via right-click
  // context menu or double-click; touch reaches edit via swipe-right.
  const handleRowClick = useCallback(() => {
    if (selectionActive) {
      // Containers keep their chevron (a separate click target below) so a
      // block can still be opened to reach the children you want to select.
      if (isSelectable) selection.toggle(indexPath);
      return;
    }
    if (isContainer) handleToggleExpand();
  }, [
    selectionActive,
    isSelectable,
    selection,
    indexPath,
    isContainer,
    handleToggleExpand,
  ]);

  // Mute rest rows visually so active Work rows stand out at a glance.
  const isLeafRestRow =
    isLeaf &&
    String(interval.type || "").toLowerCase() === "rest" &&
    !isTransitionLabel(interval.label);

  // Visible children, with original index preserved so edit/delete by
  // indexPath still target the correct node when summary-hidden children
  // are skipped.
  const visibleChildren = hasChildren
    ? interval.children
        .map((child, idx) => ({ child, idx }))
        .filter(({ child }) => !isHiddenInSummary(child))
    : [];

  const handleEdit = useCallback(() => {
    onOpenDetail?.(interval, indexPath, isContainer);
  }, [onOpenDetail, interval, indexPath, isContainer]);

  const handleDelete = useCallback(() => {
    onDelete?.(indexPath, interval);
  }, [onDelete, indexPath, interval]);

  const handleDuplicate = useCallback(() => {
    onDuplicate?.(indexPath);
  }, [onDuplicate, indexPath]);

  const handleStartSelect = useCallback(() => {
    selection?.start(indexPath);
  }, [selection, indexPath]);

  const handleSplit = useCallback(() => {
    blockActions?.split?.(indexPath, interval);
  }, [blockActions, indexPath, interval]);

  const handleUngroup = useCallback(() => {
    blockActions?.ungroup?.(indexPath);
  }, [blockActions, indexPath]);

  const rowBody = (
    <div
      ref={sortable ? setNodeRef : undefined}
      style={{
        ...sortableStyle,
        WebkitTouchCallout: "none",
        transform:
          swipeOffset !== 0 ? `translateX(${swipeOffset}px)` : undefined,
        transition: swipeEnabled
          ? swiping
            ? "none"
            : "transform 160ms ease"
          : undefined,
      }}
      className={cn(
        locked && "opacity-50",
        // The phase accent is a clipped bar (below), not `border-l-4`. A 4px
        // left border meeting a 1px top border across a shared 6px outer
        // radius mitres into a visible blob at each corner — the "red dots".
        // No border-width juggling fixes that; only not drawing the accent as
        // a border does. overflow-hidden is what shapes the bar (and the swipe
        // backdrops) to the card's inner curve, radius-exactly.
        "rounded-md border border-border/50 group select-none relative overflow-hidden",
        backgroundClass,
        isLeafRestRow && "opacity-60",
        // Inset rings, not offset ones: these cards render inside scroll
        // containers (the history modal clips horizontally), where a ring
        // drawn outside the card is cropped away at the left edge.
        isNestTarget && "ring-2 ring-inset ring-primary/60",
        isSelected && "ring-2 ring-inset ring-primary",
      )}
    >
      {/* Phase accent stripe. First child so the swipe backdrops paint over
          it, and square-cornered so the card's own clip rounds its ends. */}
      <span
        aria-hidden
        className={cn(
          "absolute inset-y-0 left-0 w-1 pointer-events-none",
          theme.bg,
        )}
      />
      {/* Swipe backdrops. Mounted only mid-swipe, square-cornered, and shaped
          entirely by the card's overflow-hidden — rounding them here as well
          only reintroduces a radius that has to agree with the card's inner
          curve, which is what put a coloured dot in each corner. */}
      {swipeOffset < 0 && (
        <div
          className="absolute inset-y-0 right-0 flex items-center justify-end pr-3 gap-1 bg-destructive text-destructive-foreground text-xs font-medium overflow-hidden whitespace-nowrap pointer-events-none"
          style={{ width: Math.min(-swipeOffset + 8, swipeMaxReveal + 8) }}
          aria-hidden
        >
          Delete <Trash2 className="w-4 h-4" />
        </div>
      )}
      {swipeOffset > 0 && (
        <div
          className="absolute inset-y-0 left-0 flex items-center justify-start pl-3 gap-1 bg-cardio text-white text-xs font-medium overflow-hidden whitespace-nowrap pointer-events-none"
          style={{ width: Math.min(swipeOffset + 8, swipeMaxReveal + 8) }}
          aria-hidden
        >
          <Pencil className="w-4 h-4" /> Edit
        </div>
      )}
      {sortable && !isDragOverlay && !selectionActive && (
        <button
          type="button"
          ref={setActivatorNodeRef}
          aria-label="Drag to reorder"
          className={cn(
            "absolute left-1 top-0 bottom-0 w-5 flex justify-center cursor-grab active:cursor-grabbing z-10 text-muted-foreground/40 hover:text-muted-foreground transition-colors",
            // On an expanded group the card spans its children, so pin the
            // grip to the header row it actually drags.
            isContainer && expanded ? "items-start pt-3" : "items-center",
          )}
          style={{ touchAction: "none" }}
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="w-3.5 h-3.5" />
        </button>
      )}
      {/* Header row. Carries the swipe handlers — children render in a
          sibling subtree below, so their gestures never reach this card.
          ContextMenuTrigger (desktop only) wraps the whole card via the
          outer conditional below — the drag-handle sibling above is
          deliberately outside the trigger. */}
      <div
        className={cn(
          "flex items-center gap-2 py-2 pr-2 transition-colors cursor-pointer",
          sortable && !isDragOverlay ? "pl-7" : "pl-3",
          theme.hover,
          // Rows outside the selection's parent can't join it — dimmed on the
          // header row alone, never the card, so dimming a block never dims
          // the child inside it that IS selected.
          selectionActive && !isSelectable && !isSelected && "opacity-40",
        )}
        style={swipeEnabled ? { touchAction: "pan-y" } : undefined}
        role="button"
        tabIndex={0}
        // The checkbox is decorative; the row itself is the control, so the
        // selected state has to be announced here.
        aria-pressed={selectionActive && isSelectable ? isSelected : undefined}
        onClick={handleRowClick}
        {...(swipeEnabled ? swipeHandlers : {})}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            onOpenDetail?.(interval, indexPath, isContainer);
          }
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          handleEdit();
        }}
      >
        {selectionActive && (
          <div
            aria-hidden
            className={cn(
              "w-4 h-4 rounded-[4px] border flex items-center justify-center flex-shrink-0 transition-colors",
              isSelected
                ? "bg-primary border-primary text-primary-foreground"
                : "border-muted-foreground/50",
            )}
          >
            {isSelected && <Check className="w-3 h-3" />}
          </div>
        )}

        {isContainer ? (
          selectionActive ? (
            // Selecting hands the row tap to the checkbox, so the chevron
            // becomes the only way in — it has to be its own click target.
            <button
              type="button"
              aria-label={expanded ? "Collapse block" : "Expand block"}
              onClick={(e) => {
                e.stopPropagation();
                handleToggleExpand();
              }}
              className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors"
            >
              {expanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>
          ) : (
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-muted-foreground">
              {expanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </div>
          )
        ) : (
          <div className="w-1 flex-shrink-0" />
        )}

        <div className={cn("flex-shrink-0", theme.text)}>
          {React.cloneElement(icon, { className: "w-4 h-4" })}
        </div>

        <div className="flex-grow min-w-0">
          <div
            className="font-medium text-foreground truncate text-sm"
            title={label}
          >
            {label}
          </div>
          {perSetLoads && (
            <div className="mt-0.5 text-[10px] font-semibold tabular-nums tracking-wide text-muted-foreground truncate">
              {perSetLoads}
            </div>
          )}
          {isLeaf && interval.linkToPrevious && (
            <div className="mt-0.5 text-[10px] tracking-wide text-muted-foreground truncate">
              {"\u21b3 Linked to previous: one Done for both"}
            </div>
          )}
          {isLeaf && !isLeafRestRow && interval.intensityTarget && (
            <div className="mt-0.5 text-[10px] font-medium tracking-wide text-primary/70 truncate">
              Target: {interval.intensityTarget}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 text-sm flex-shrink-0">
          {showRoundBreakdown ? (
            <BlockTimePopover
              summary={roundSummary}
              totalLabel={formattedTotalDuration}
              disabled={!hasRoundSummary}
            >
              <button
                type="button"
                onClick={(e) => e.stopPropagation()}
                className="flex items-center gap-1.5 text-muted-foreground tabular-nums rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                title={`Total ${formattedTotalDuration} · ${rounds} rounds, first round ${perRoundCompact}`}
              >
                <Clock className="w-3.5 h-3.5" />
                <span className="text-xs font-semibold text-foreground">
                  {totalCompact}
                </span>
                <span className="text-xs opacity-60">|</span>
                <span className="text-xs">
                  {rounds}× {perRoundApprox ? "~" : ""}
                  {perRoundCompact}
                </span>
              </button>
            </BlockTimePopover>
          ) : (
            formattedTotalDuration &&
            hasChildren &&
            !isTimeCappedGroup && (
              <div
                className="flex items-center gap-1 text-muted-foreground"
                title="Total estimated time"
              >
                <Clock className="w-3.5 h-3.5" />
                <span className="text-xs">{formattedTotalDuration}</span>
              </div>
            )
          )}

          {denseString && !showRoundBreakdown && (
            <RepEstimatePopover
              repEstimate={repEstimate}
              value={interval.value}
              unit={interval.unit}
              schemeValues={perSetValues}
            >
              <button
                type="button"
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                className={cn(
                  "flex items-center gap-1.5 text-sm rounded px-1",
                  repEstimate && repEstimate.totalSec >= 5
                    ? "cursor-pointer hover:bg-foreground/5"
                    : "cursor-default",
                )}
                title={verboseString}
              >
                <span className=" tracking-tight text-foreground">
                  {denseString}
                </span>
              </button>
            </RepEstimatePopover>
          )}

          {/* ⋯ actions menu — the primary, pointer-agnostic affordance, same
              as the programme timeline rows. Right-click (desktop) and swipe
              (touch) stay as shortcuts to the same actions. */}
          {!isDragOverlay &&
            !selectionActive &&
            !locked &&
            (onDelete || onDuplicate || selection?.start) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  onDoubleClick={(e) => e.stopPropagation()}
                  aria-label="Step actions"
                  title="Step actions"
                  className="p-1 rounded text-muted-foreground/50 hover:text-foreground transition-colors"
                >
                  <MoreVertical className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="z-[70] w-44">
                {onOpenDetail && (
                  <DropdownMenuItem onSelect={handleEdit}>
                    <Pencil className="w-3.5 h-3.5 mr-2" />
                    Edit
                  </DropdownMenuItem>
                )}
                {onDuplicate && (
                  <DropdownMenuItem onSelect={handleDuplicate}>
                    <Copy className="w-3.5 h-3.5 mr-2" />
                    Duplicate
                  </DropdownMenuItem>
                )}
                {selection?.start && (
                  <DropdownMenuItem onSelect={handleStartSelect}>
                    <Layers className="w-3.5 h-3.5 mr-2" />
                    Group into block
                  </DropdownMenuItem>
                )}
                {canSplit && (
                  <DropdownMenuItem onSelect={handleSplit}>
                    <Split className="w-3.5 h-3.5 mr-2" />
                    Split into rounds
                  </DropdownMenuItem>
                )}
                {canUngroup && (
                  <DropdownMenuItem onSelect={handleUngroup}>
                    <Ungroup className="w-3.5 h-3.5 mr-2" />
                    Ungroup
                  </DropdownMenuItem>
                )}
                {onDelete && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={handleDelete}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {isContainer && expanded && (
        <div className="pt-1 pr-1.5 pb-1.5 pl-2 ml-2 mb-1.5">
          {hasChildren ? (
            <SortableContext
              items={visibleChildren.map(
                ({ child, idx }) => child.id ?? `card-${[...indexPath, idx].join(".")}`,
              )}
              strategy={verticalListSortingStrategy}
            >
              <RowGap
                onInsert={
                  onInsertNode ? () => onInsertNode(indexPath, 0) : null
                }
                parentIndexPath={indexPath}
                insertIndex={0}
                droppable={sortable}
              />
              {visibleChildren.map(({ child, idx }) => (
                <React.Fragment
                  key={(child.id || child.label || "child") + "-" + idx}
                >
                  {isSetupNode(child) ? (
                    <SetupDivider node={child} indexPath={[...indexPath, idx]} />
                  ) : (
                    <>
                    <SkippedSetup owner={child} ownerIndexPath={[...indexPath, idx]} />
                    <IntervalCard
                      interval={child}
                      depth={depth + 1}
                      indexPath={[...indexPath, idx]}
                      onOpenDetail={onOpenDetail}
                      onInsertNode={onInsertNode}
                      onDelete={onDelete}
                      onDuplicate={onDuplicate}
                      sortable={sortable}
                      collapseGroups={collapseGroups}
                    />
                    </>
                  )}
                  <RowGap
                    onInsert={
                      onInsertNode
                        ? () => onInsertNode(indexPath, idx + 1)
                        : null
                    }
                    parentIndexPath={indexPath}
                    insertIndex={idx + 1}
                    droppable={sortable}
                  />
                </React.Fragment>
              ))}
            </SortableContext>
          ) : (
            // Empty container: a visible add affordance (the hover-revealed
            // gap "+" is invisible on touch). Drops can also nest into the
            // group via the row's "into" band.
            onInsertNode && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onInsertNode(indexPath, 0);
                }}
                className="w-full flex items-center justify-center gap-1.5 py-2 my-1 rounded-md border border-dashed border-border text-xs text-muted-foreground hover:text-foreground hover:border-primary/50 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                Add step
              </button>
            )
          )}
        </div>
      )}
    </div>
  );

  // Desktop only: wrap row in a Radix ContextMenu for right-click Edit /
  // Duplicate / Delete. Touch users have swipe gestures instead — no
  // long-press menu, so no ContextMenu tree at all.
  if (isTouchDevice || isDragOverlay || selectionActive || locked) {
    return rowBody;
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{rowBody}</ContextMenuTrigger>
      <ContextMenuContent className="z-[70] w-44">
        <ContextMenuItem onSelect={handleEdit}>
          <Pencil className="w-3.5 h-3.5 mr-2" />
          Edit
        </ContextMenuItem>
        {onDuplicate && (
          <ContextMenuItem onSelect={handleDuplicate}>
            <Copy className="w-3.5 h-3.5 mr-2" />
            Duplicate
          </ContextMenuItem>
        )}
        {selection?.start && (
          <ContextMenuItem onSelect={handleStartSelect}>
            <Layers className="w-3.5 h-3.5 mr-2" />
            Group into block
          </ContextMenuItem>
        )}
        {canSplit && (
          <ContextMenuItem onSelect={handleSplit}>
            <Split className="w-3.5 h-3.5 mr-2" />
            Split into rounds
          </ContextMenuItem>
        )}
        {canUngroup && (
          <ContextMenuItem onSelect={handleUngroup}>
            <Ungroup className="w-3.5 h-3.5 mr-2" />
            Ungroup
          </ContextMenuItem>
        )}
        {onDelete && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem
              onSelect={handleDelete}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="w-3.5 h-3.5 mr-2" />
              Delete
            </ContextMenuItem>
          </>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
};

// Renders a chip showing the transition + block-setup seconds baked into the
// plan plus the location it was generated for. If the user's current defaults
// would resolve to different values, surface a quiet drift hint so users
// understand why the math may not reflect their current environment.
const LocationTransitionChip = ({ plan }) => {
  const [workoutSettings] = useWorkoutSettings();
  const userProfile = useUserProfile();
  const meta = plan?.meta;
  if (!meta || meta.transitionSeconds == null) return null;
  const bakedTransition = Number(meta.transitionSeconds);
  const bakedSetup =
    meta.blockSetupSeconds != null ? Number(meta.blockSetupSeconds) : null;
  const defaultLoc = (userProfile?.locationPresets || []).find(
    (lp) => lp.is_default,
  );
  const currentTransition =
    defaultLoc && defaultLoc.transition_seconds != null
      ? Number(defaultLoc.transition_seconds)
      : Number.isFinite(Number(workoutSettings?.defaultTransitionSeconds))
        ? Number(workoutSettings.defaultTransitionSeconds)
        : null;
  const currentSetup =
    defaultLoc && defaultLoc.block_setup_seconds != null
      ? Number(defaultLoc.block_setup_seconds)
      : Number.isFinite(Number(workoutSettings?.defaultBlockSetupSeconds))
        ? Number(workoutSettings.defaultBlockSetupSeconds)
        : null;
  const transitionDrifted =
    currentTransition != null &&
    Number.isFinite(currentTransition) &&
    currentTransition !== bakedTransition;
  const setupDrifted =
    bakedSetup != null &&
    currentSetup != null &&
    Number.isFinite(currentSetup) &&
    currentSetup !== bakedSetup;
  const drifted = transitionDrifted || setupDrifted;
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
      <MapPin className="w-3.5 h-3.5" />
      <span>
        {meta.locationName ? `${meta.locationName} · ` : ""}transitions{" "}
        {bakedTransition}s
        {bakedSetup != null && (
          <>
            {" · "}setup {bakedSetup}s
          </>
        )}
      </span>
      {drifted && (
        <span className="text-amber-500/80">
          (current defaults differ — regenerate to apply)
        </span>
      )}
    </div>
  );
};

// Surfaces what generation could not fix (see blockingValidationIssues).
const PlanValidationNotice = ({ plan }) => {
  const blocking = blockingValidationIssues(plan);
  if (blocking.length === 0) return null;
  return (
    <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200/90">
      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-400" />
      <div className="space-y-1">
        <p className="font-medium text-amber-100">
          This session did not come out as asked — regenerate or adjust it by
          hand.
        </p>
        <ul className="space-y-0.5">
          {blocking.map((issue, i) => (
            <li key={issue.code ? `${issue.code}-${i}` : i}>{issue.message}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const SessionPlanDisplay = ({
  plan,
  editable = false,
  onEditNode,
  onDeleteNode,
  onAddNode,
  onMoveNode,
  onDuplicateNode,
  onWrapNodes,
  onSplitNode,
  onUngroupNode,
  llmSettings,
  userProfile,
  // Off where the caller's header already shows the total and end time (the
  // Session tab, #111), so the same line isn't printed twice.
  showTotal = true,
  // Ids of steps that can't be edited (finished, mid-session: #175).
  lockedNodeIds = null,
}) => {
  const totalSeconds = useTotalDuration(plan);
  // The mouse wording (right-click, double-click, hover) only where there is
  // a mouse: a touch screen, or anything without a fine hovering pointer,
  // gets the touch wording.
  const finePointer = useMediaQuery("(hover: hover) and (pointer: fine)");
  const { showToast } = useToast();
  const wrapEnabled = Boolean(editable && onWrapNodes);

  // The block whose rounds are being named, or null when the sheet is shut.
  const [splitTarget, setSplitTarget] = useState(null);
  const blockActions = useMemo(() => {
    if (!editable || (!onSplitNode && !onUngroupNode)) return null;
    return {
      split: onSplitNode
        ? (indexPath, node) => setSplitTarget({ indexPath, node })
        : null,
      ungroup: onUngroupNode || null,
    };
  }, [editable, onSplitNode, onUngroupNode]);

  // --- Multi-select, for collecting loose steps into a block ---
  // `null` means not selecting; an array (possibly empty) means the mode is on,
  // so one piece of state carries both. Every mutating affordance stands down
  // while it's on — no drag, no insert gaps, no delete — which is also what
  // keeps the stored index paths valid until the wrap commits.
  const [selectionPaths, setSelectionPaths] = useState(null);
  const selectionActive = Array.isArray(selectionPaths);
  const selectionCount = selectionPaths?.length ?? 0;

  const sortableEnabled = Boolean(editable && onMoveNode) && !selectionActive;
  const [activeDragNode, setActiveDragNode] = useState(null);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    if (typeof navigator === "undefined") return;
    setIsTouchDevice(navigator.maxTouchPoints > 0);
  }, []);

  // PointerSensor with a small distance threshold so taps still register
  // as taps; touch users initiate drag with motion from the handle.
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = useCallback(
    (event) => {
      // Walk the plan to find the node by indexPath so the overlay can
      // render a faithful clone.
      const indexPath = event?.active?.data?.current?.indexPath;
      if (!Array.isArray(indexPath) || !plan) return;
      let node = plan;
      for (const i of indexPath) {
        node = node?.children?.[i];
        if (!node) return;
      }
      setActiveDragNode({ node, indexPath });
    },
    [plan],
  );

  // Live drop hint — resolved on every drag move with the same logic as the
  // final drop, so the indicator can never disagree with where the node
  // actually lands. Shallow-compared to avoid re-render churn per pointer move.
  const [dropHint, setDropHint] = useState(null);
  const handleDragMove = useCallback((event) => {
    const target = resolveDropTarget(event);
    const next = !target
      ? null
      : target.into
        ? { kind: "into", key: target.toParentIndexPath.join(".") }
        : {
            kind: "gap",
            parentKey: target.toParentIndexPath.join("."),
            index: target.toIndex,
          };
    setDropHint((prev) => {
      if (prev === next) return prev;
      if (
        prev &&
        next &&
        prev.kind === next.kind &&
        prev.key === next.key &&
        prev.parentKey === next.parentKey &&
        prev.index === next.index
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  const handleDragEnd = useCallback(
    (event) => {
      setActiveDragNode(null);
      setDropHint(null);
      if (!onMoveNode) return;
      const target = resolveDropTarget(event);
      if (!target) return;
      onMoveNode(target.fromIndexPath, target.toParentIndexPath, target.toIndex);
    },
    [onMoveNode],
  );

  const handleDragCancel = useCallback(() => {
    setActiveDragNode(null);
    setDropHint(null);
  }, []);

  // Collapse every group while a TOP-LEVEL group/circuit is being dragged:
  // block-level reordering happens over a short, stable list instead of a
  // huge scrolling tree. Leaf drags keep groups open for precise in-group
  // placement. Restricted to top-level drags because collapsing while
  // dragging a NESTED group would unmount the dragged node itself.
  const collapseGroups = Boolean(
    activeDragNode &&
      activeDragNode.indexPath?.length === 1 &&
      (activeDragNode.node?.children?.length > 0 ||
        isContainerType(activeDragNode.node?.type)),
  );

  // The Setup divider whose sheet is open, by index path (null when shut).
  // Reflow keeps a Setup at its path while only its length changes, so the
  // sheet reads the live node from the plan rather than a stale copy.
  const [setupPath, setSetupPath] = useState(null);
  const setupNode = useMemo(() => {
    if (!setupPath || !plan) return null;
    let node = plan;
    for (const i of setupPath) node = node?.children?.[i];
    return isSetupNode(node) ? node : null;
  }, [plan, setupPath]);
  const setupActions = useMemo(() => {
    if (!editable || !onEditNode || selectionActive) return null;
    return {
      open: (indexPath) => setSetupPath(indexPath),
      restore: (ownerIndexPath) =>
        onEditNode(ownerIndexPath, { skipSetup: false }),
    };
  }, [editable, onEditNode, selectionActive]);

  const [detailNode, setDetailNode] = useState(null);
  const [detailIndexPath, setDetailIndexPath] = useState(null);
  const [detailHasChildren, setDetailHasChildren] = useState(false);
  const [detailSetCount, setDetailSetCount] = useState(1);
  const [detailOpen, setDetailOpen] = useState(false);
  // "edit" or "create". In create mode, Save routes to onAddNode at the
  // pendingInsert location and Cancel discards.
  const [detailMode, setDetailMode] = useState("edit");
  const [pendingInsert, setPendingInsert] = useState(null);

  const handleOpenDetail = useCallback(
    (node, indexPath, hasChildren) => {
      setDetailMode("edit");
      setPendingInsert(null);
      setDetailNode(node);
      setDetailIndexPath(indexPath);
      setDetailHasChildren(hasChildren);
      // The node itself doesn't know how often it repeats — that lives on the
      // Group above it — so the per-set editor is sized from the tree (#69).
      setDetailSetCount(enclosingSetCount(plan, indexPath));
      setDetailOpen(true);
    },
    [plan],
  );

  // New behaviour: clicking `+` doesn't insert immediately. It opens the
  // modal in create mode with a default node and the target location stored
  // in pendingInsert. Save commits via onAddNode; Cancel discards.
  const handleInsertNode = useCallback(
    (parentIndexPath, insertIndex) => {
      if (!onAddNode) return;
      setDetailMode("create");
      setPendingInsert({ parentIndexPath, insertIndex });
      setDetailNode({
        label: "New exercise",
        type: "Work",
        value: 30,
        unit: "s",
      });
      setDetailIndexPath(null);
      setDetailHasChildren(false);
      setDetailSetCount(1);
      setDetailOpen(true);
    },
    [onAddNode],
  );

  const handleCreate = useCallback(
    (finalNode) => {
      if (!pendingInsert || !onAddNode) return;
      onAddNode(
        pendingInsert.parentIndexPath,
        finalNode,
        pendingInsert.insertIndex,
      );
      setPendingInsert(null);
    },
    [pendingInsert, onAddNode],
  );

  const startSelection = useCallback((indexPath) => {
    setSelectionPaths(
      Array.isArray(indexPath) && indexPath.length > 0 ? [indexPath] : [],
    );
  }, []);

  const cancelSelection = useCallback(() => setSelectionPaths(null), []);

  const toggleSelection = useCallback((indexPath) => {
    setSelectionPaths((prev) => {
      if (!prev) return prev;
      const key = indexPath.join(".");
      const without = prev.filter((path) => path.join(".") !== key);
      return without.length === prev.length ? [...prev, indexPath] : without;
    });
  }, []);

  const selectedKeys = useMemo(
    () => new Set((selectionPaths || []).map((path) => path.join("."))),
    [selectionPaths],
  );

  // The parent the selection is locked to, once there is one. A block occupies
  // a single slot in a single parent, so rows from anywhere else can't join.
  const selectionParentKey = useMemo(() => {
    if (!selectionPaths || selectionPaths.length === 0) return null;
    const parent = sharedParentPath(selectionPaths);
    return parent ? parent.join(".") : null;
  }, [selectionPaths]);

  const canSelect = useCallback(
    (indexPath) => {
      if (!Array.isArray(indexPath) || indexPath.length === 0) return false;
      if (!selectionPaths || selectionPaths.length === 0) return true;
      return indexPath.slice(0, -1).join(".") === selectionParentKey;
    },
    [selectionPaths, selectionParentKey],
  );

  const selectionValue = useMemo(
    () =>
      wrapEnabled
        ? {
            active: selectionActive,
            selected: selectedKeys,
            canSelect,
            toggle: toggleSelection,
            start: selectionActive ? null : startSelection,
          }
        : null,
    [
      wrapEnabled,
      selectionActive,
      selectedKeys,
      canSelect,
      toggleSelection,
      startSelection,
    ],
  );

  // Commit the selection into a new block, then open it for naming — the block
  // arrives with a placeholder label, and hunting for it in the tree afterwards
  // is the one step that would make this feel unfinished.
  const handleWrapSelection = useCallback(() => {
    if (!onWrapNodes || !selectionPaths || selectionPaths.length < 2) return;
    const parentPath = sharedParentPath(selectionPaths);
    if (!parentPath) return;
    const groupNode = {
      id: genStableId(),
      type: "Group",
      label: "New block",
      unit: "rounds",
      value: 1,
    };
    onWrapNodes(selectionPaths, groupNode);
    setSelectionPaths(null);
    // The block lands at the topmost selected index (see wrapNodesInGroup).
    const insertAt = Math.min(
      ...selectionPaths.map((path) => path[path.length - 1]),
    );
    const groupIndexPath = [...parentPath, insertAt];
    setDetailMode("edit");
    setDetailNode(groupNode);
    setDetailIndexPath(groupIndexPath);
    setDetailHasChildren(true);
    // The wrap doesn't touch the parent, so the pre-wrap plan still answers
    // "how many sets does this run for" correctly.
    setDetailSetCount(enclosingSetCount(plan, groupIndexPath));
    setDetailOpen(true);
  }, [onWrapNodes, selectionPaths, plan]);

  // Single delete codepath: row trash icon, context menu, and (later) swipe
  // all funnel through this. Fires onDeleteNode and shows a 5s undo toast
  // that re-inserts the original snapshot at the same indexPath.
  const handleDeleteWithUndo = useCallback(
    (indexPath, node) => {
      if (!onDeleteNode) return;
      onDeleteNode(indexPath);
      if (!showToast) return;
      const label = node?.label || node?.exercise_name || "step";
      showToast({
        title: `Deleted ${label}`,
        action: onAddNode
          ? {
              label: "Undo",
              onClick: () => {
                onAddNode(
                  indexPath.slice(0, -1),
                  node,
                  indexPath[indexPath.length - 1],
                );
              },
            }
          : undefined,
        duration: 5000,
      });
    },
    [onDeleteNode, onAddNode, showToast],
  );

  if (!plan || !plan.children || plan.children.length === 0) {
    // An empty plan being edited (a record saved without its steps) needs a
    // way in: there are no rows to hang an insert gap or a ⋯ menu off.
    if (editable && onAddNode) {
      return (
        <div className="py-6 text-center">
          <p className="mb-3 text-sm text-muted-foreground">No steps yet.</p>
          <Button variant="outline" size="sm" onClick={() => handleInsertNode([], 0)}>
            <Plus className="mr-1.5 h-4 w-4" />
            Add a step
          </Button>
          <ExerciseDetailDialog
            open={detailOpen}
            onOpenChange={setDetailOpen}
            node={detailNode}
            indexPath={detailIndexPath}
            hasChildren={detailHasChildren}
            setCount={detailSetCount}
            mode={detailMode}
            onCreate={detailMode === "create" ? handleCreate : undefined}
            llmSettings={llmSettings}
            userProfile={userProfile}
          />
        </div>
      );
    }
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>Your generated session will appear here.</p>
        <p className="text-sm">
          Build one from the Session tab.
        </p>
      </div>
    );
  }

  const formattedTotal = formatDuration(totalSeconds);
  const projectedEnd =
    totalSeconds > 0
      ? new Date(Date.now() + totalSeconds * 1000).toLocaleTimeString([], {
          hour: "numeric",
          minute: "2-digit",
        })
      : null;

  return (
    <div className="w-full space-y-3">
      {/* Title/description intentionally NOT rendered here — the session
          header card (ActiveState) owns them, including the rename control.
          Rendering them twice made the rename affordance ambiguous. */}
      {/* Outside the duration block below: the worst plans are the ones with
          no timed content at all, and those render no total to hang it off. */}
      <PlanValidationNotice plan={plan} />
      {formattedTotal && (
        <div className={showTotal ? "py-2 space-y-1" : "empty:hidden"}>
          {showTotal && (
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              <span>
                Total:{" "}
                <span className="font-semibold text-foreground">
                  {formattedTotal}
                </span>
              </span>
            </div>
            {projectedEnd && (
              <div className="flex items-center gap-1.5">
                <span>
                  Ends at:{" "}
                  <span className="font-semibold text-foreground">
                    {projectedEnd}
                  </span>
                </span>
              </div>
            )}
          </div>
          )}
          <LocationTransitionChip plan={plan} />
        </div>
      )}
      {editable && !selectionActive && (
        <div className="flex items-start justify-between gap-3 px-1">
          <div className="flex items-start gap-1.5 text-xs text-muted-foreground/80">
            <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span>
              {isTouchDevice || !finePointer
                ? "Tap ⋯ on a step for options. Swipe right to edit, left to delete. Drag the handle to reorder."
                : "Use ⋯ or right-click a step or block for options. Double-click to edit. Hover between steps to insert."}
            </span>
          </div>
          {wrapEnabled && (
            <button
              type="button"
              onClick={() => startSelection(null)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
            >
              <Layers className="w-3.5 h-3.5" />
              Group steps
            </button>
          )}
        </div>
      )}
      {selectionActive && (
        // Sticky: on a long plan the steps you are collecting scroll well past
        // the top of the list, and a Group button you have to scroll back for
        // is a Group button you forget is there.
        <div className="sticky top-0 z-30 flex items-center justify-between gap-3 rounded-md border border-primary/40 bg-card px-3 py-2">
          <span className="text-xs text-muted-foreground min-w-0">
            {selectionCount === 0
              ? "Pick the steps to gather into a block."
              : selectionCount === 1
                ? "1 step selected — pick at least one more."
                : `${selectionCount} steps selected.`}
          </span>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button variant="ghost" size="sm" onClick={cancelSelection}>
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={selectionCount < 2}
              onClick={handleWrapSelection}
            >
              <Layers className="w-3.5 h-3.5 mr-1.5" />
              Group
            </Button>
          </div>
        </div>
      )}
      <LockedContext.Provider value={lockedNodeIds}>
      <SelectionContext.Provider value={selectionValue}>
      <SplitContext.Provider value={blockActions}>
      <SetupContext.Provider value={setupActions}>
      <DropHintContext.Provider value={dropHint}>
      <DndContext
        sensors={sensors}
        collisionDetection={dndCollision}
        // Re-measure droppables while dragging: group collapse on drag start
        // (and any autoscroll-driven layout shift) changes every rect, and
        // drag-start-only measurement would band against stale geometry.
        measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext
          items={plan.children
            .map((child, idx) => ({ child, idx }))
            .filter(({ child }) => !isHiddenInSummary(child))
            .map(({ child, idx }) => child.id ?? `card-${idx}`)}
          strategy={verticalListSortingStrategy}
        >
          <div>
            <RowGap
              onInsert={
                editable && onAddNode && !selectionActive
                  ? () => handleInsertNode([], 0)
                  : null
              }
              parentIndexPath={[]}
              insertIndex={0}
              droppable={sortableEnabled}
            />
            {plan.children
              .map((child, idx) => ({ child, idx }))
              // Always hide short intra-block transitions from the summary —
              // queued at runtime but visual noise on the plan view.
              .filter(({ child }) => !isHiddenInSummary(child))
              .map(({ child, idx }) => (
                <React.Fragment
                  key={(child.id || child.label || "child") + "-" + idx}
                >
                  {isSetupNode(child) ? (
                    <SetupDivider node={child} indexPath={[idx]} />
                  ) : (
                    <>
                    <SkippedSetup owner={child} ownerIndexPath={[idx]} />
                    <IntervalCard
                      interval={child}
                      depth={0}
                      indexPath={[idx]}
                      onOpenDetail={handleOpenDetail}
                      onInsertNode={
                        editable && onAddNode && !selectionActive
                          ? handleInsertNode
                          : null
                      }
                      onDelete={
                        editable && onDeleteNode ? handleDeleteWithUndo : null
                      }
                      onDuplicate={
                        editable && onDuplicateNode ? onDuplicateNode : null
                      }
                      sortable={sortableEnabled}
                      collapseGroups={collapseGroups}
                    />
                    </>
                  )}
                  <RowGap
                    onInsert={
                      editable && onAddNode && !selectionActive
                        ? () => handleInsertNode([], idx + 1)
                        : null
                    }
                    parentIndexPath={[]}
                    insertIndex={idx + 1}
                    droppable={sortableEnabled}
                  />
                </React.Fragment>
              ))}
          </div>
        </SortableContext>
        {/* Portal to body: DragOverlay uses position:fixed, but any ancestor
            with transform / filter / backdrop-filter / perspective / will-change
            (e.g. the parent Card's `backdrop-blur` in ActiveState) becomes
            the containing block for fixed-position children, throwing off
            the overlay's viewport-coordinate placement. Rendering through a
            portal at <body> avoids any wrapper creating a containing block,
            so the floating clone tracks the cursor / touch point. React
            context (DndContext) propagates through portals, so this is
            transparent to dnd-kit. */}
        {typeof document !== "undefined" &&
          createPortal(
            <DragOverlay>
              {activeDragNode ? (
                <div className="pointer-events-none">
                  {/* Ghost groups render collapsed — a full expanded-group
                      clone is an unwieldy drag ghost. */}
                  <IntervalCard
                    interval={activeDragNode.node}
                    depth={0}
                    indexPath={activeDragNode.indexPath}
                    isDragOverlay
                    collapseGroups
                  />
                </div>
              ) : null}
            </DragOverlay>,
            document.body,
          )}
      </DndContext>
      </DropHintContext.Provider>
      </SetupContext.Provider>
      </SplitContext.Provider>
      </SelectionContext.Provider>
      </LockedContext.Provider>

      <SetupSheet
        node={setupNode}
        onOpenChange={(open) => !open && setSetupPath(null)}
        onChangeSeconds={(v) => onEditNode?.(setupPath, { value: v, unit: "s" })}
        onSkip={() => {
          // The skip lives on the step after the Setup (its owner); the
          // reflow that follows the edit takes the Setup out.
          const owner = [...setupPath.slice(0, -1), setupPath[setupPath.length - 1] + 1];
          onEditNode?.(owner, { skipSetup: true });
          setSetupPath(null);
        }}
      />

      <SplitRoundsSheet
        node={splitTarget?.node || null}
        onOpenChange={(open) => !open && setSplitTarget(null)}
        onSplit={(names) => {
          onSplitNode(splitTarget.indexPath, names);
          setSplitTarget(null);
        }}
      />

      <ExerciseDetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        node={detailNode}
        indexPath={detailIndexPath}
        hasChildren={detailHasChildren}
        setCount={detailSetCount}
        mode={detailMode}
        onSave={editable ? onEditNode : undefined}
        onCreate={editable && detailMode === "create" ? handleCreate : undefined}
        onDelete={
          editable && detailMode === "edit" && onDeleteNode
            ? onDeleteNode
            : undefined
        }
        onDuplicate={
          editable && detailMode === "edit" && onDuplicateNode
            ? onDuplicateNode
            : undefined
        }
        llmSettings={llmSettings}
        userProfile={userProfile}
      />
    </div>
  );
};

export default SessionPlanDisplay;
